import type { ProductResponse } from '~/clients'
import { z } from 'zod'
import { useApiClient } from '~/composables/api/api'
import type { MutationOptions } from '@tanstack/query-core'
import { zodTranslation } from '~/utils'
import { convertStringToArray } from '~/utils/inputHelper'

export const productShema = z.object({
  id: z.number().optional(),
  code: z
    .string()
    .min(1, 'Enter product code')
    .max(8, 'Product code should not be longer than 8 characters'),
  isActiveMonitoring: z.boolean(),
  isActiveForRiskAssessment: z.boolean(),
  isEuProduct: z.boolean(),
  expirationDate: z.coerce.date().optional().nullable(),
  nameAsJson: zodTranslation('Name is required.'),
  productGroupId: z
    .number()
    .nullable()
    .refine((value) => !!value, 'Select product group'),
  definitionAsJson: zodTranslation('Description is required.'),
  synonymsAsJson: zodTranslation('Synonym is required.', true).transform((val) => {
    Object.keys(val).forEach((key) => {
      val[key] = convertStringToArray(val[key])
    })
    return val
  }),
})

export type ProductForm = z.infer<typeof productShema>

export const useManageProduct = (
  type: 'create' | 'update',
  id?: number,
  mutationOptions?: MutationOptions<ProductResponse, Error, ProductForm>,
) => {
  const { mutateAsync: updateMutate, isPending: updating } = useMutation({
    mutationFn: async (body: ProductForm) => {
      return await useApiClient<ProductResponse>(`/products/${id}`, {
        method: 'PATCH',
        body,
      })
    },
    ...mutationOptions,
  })

  const { mutateAsync: createMutate, isPending: creating } = useMutation({
    mutationFn: async (body: ProductForm) => {
      return await useApiClient<ProductResponse>(`/products`, {
        method: 'POST',
        body,
      })
    },
    ...mutationOptions,
  })

  return {
    mutate: type === 'create' ? createMutate : updateMutate,
    isPending: type === 'create' ? creating : updating,
  }
}
